import * as styles from "./DataTable.css";

import { clsx } from "clsx";
import {
  Fragment,
  type JSX,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

import { Loader } from "@frontend/components/atoms/Loader";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipViewport,
  createTooltipHandle,
} from "@frontend/components/atoms/Tooltip";
import { vars } from "@frontend/styles";
import { useLingui } from "@lingui/react/macro";
import {
  type RowData,
  type RowSelectionState,
  type SortingState,
  functionalUpdate,
  useTable,
} from "@tanstack/react-table";

import type { DataTableColumnFilter, DataTableProps } from "./DataTable.types";
import { DataTableCell } from "./DataTableCell";
import { DataTableHeaderCell } from "./DataTableHeaderCell";
import { DataTableRow } from "./DataTableRow";
import { DataTableRowMenu } from "./DataTableRowMenu";
import { DataTableSelectCell } from "./DataTableSelectCell";
import { DataTableSkeleton } from "./DataTableSkeleton";
import { dataTableSpacingValue } from "./cellSizeStyles";
import { dataTableFeatures } from "./dataTableFeatures";
import { getColumnTrack } from "./getColumnTrack";

const EMPTY_SORTING: SortingState = [];

const EMPTY_FILTERS: DataTableColumnFilter[] = [];

const EMPTY_SELECTION: RowSelectionState = {};

function cssLength(value: number | string): string {
  return "number" === typeof value ? `${value}px` : value;
}

export function DataTable<TData extends RowData>({
  columns,
  data,
  getRowId,
  sorting,
  onSortingChange,
  columnFilters,
  onColumnFiltersChange,
  onSortPrefetch,
  rowSelection,
  onRowSelectionChange,
  enableRowSelection = true,
  enableMultiRowSelection = true,
  selectOnRowClick = false,
  onRowClick,
  activeRowIndex,
  detailRowId,
  renderDetail,
  rowMenu,
  onRowMenuSelect,
  loading = false,
  loadingRowCount = 5,
  loadingRowHeight,
  emptyMessage,
  size = "md",
  variant = "default",
  bordered,
  columnBorders = false,
  striped = false,
  scroll = "table",
  maxHeight,
  stickyHeader = false,
  stickyOffset,
  stickyColumn = false,
  horizontalSpacing = "md",
  verticalSpacing = "sm",
  className,
  ...props
}: DataTableProps<TData>): JSX.Element {
  const { t } = useLingui();

  const framed = bordered ?? "ghost" !== variant;
  const sortingState = sorting ?? EMPTY_SORTING;
  const filterState = columnFilters ?? EMPTY_FILTERS;
  const selectionState = rowSelection ?? EMPTY_SELECTION;
  const selectable = undefined !== onRowSelectionChange;

  const [tooltipHandle] = useState(() => createTooltipHandle<string>());

  const activeRowRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLDivElement>(null);

  const [headerHeight, setHeaderHeight] = useState(0);

  useEffect(() => {
    activeRowRef.current?.scrollIntoView({ block: "nearest" });
  }, [activeRowIndex]);

  useLayoutEffect(() => {
    const measured = stickyHeader ? (headerRef.current?.offsetHeight ?? 0) : 0;

    setHeaderHeight((current) => (current === measured ? current : measured));
  });

  function revealFirstRow() {
    const scrollArea = scrollAreaRef.current;
    const tableElement = tableRef.current;
    const header = headerRef.current;

    if (!stickyHeader || !scrollArea || !tableElement || !header) {
      return;
    }

    if ("table" === scroll && undefined !== maxHeight) {
      scrollArea.scrollTop = 0;

      return;
    }

    if (
      header.getBoundingClientRect().top >
      tableElement.getBoundingClientRect().top + 1
    ) {
      tableElement.scrollIntoView({ block: "start" });
    }
  }

  const table = useTable({
    features: dataTableFeatures,
    columns,
    data,
    getRowId,
    manualSorting: true,
    state: { sorting: sortingState, rowSelection: selectionState },
    onSortingChange: (updater) => {
      revealFirstRow();
      onSortingChange?.(functionalUpdate(updater, sortingState));
    },
    enableRowSelection:
      "function" === typeof enableRowSelection
        ? (row) => enableRowSelection(row.original)
        : enableRowSelection,
    enableMultiRowSelection,
    onRowSelectionChange: (updater) => {
      onRowSelectionChange?.(functionalUpdate(updater, selectionState));
    },
  });

  const rows = table.getRowModel().rows;

  const ownsScroll = "table" === scroll;

  const activeRowInset = stickyHeader
    ? `calc(${cssLength(stickyOffset ?? 0)} + ${headerHeight}px)`
    : undefined;

  const showSkeleton = loading && rows.length === 0;

  const leafColumnIds = table.getAllLeafColumns().map((column) => column.id);

  const selectTrack = `calc(${vars.dimension[size]} + ${dataTableSpacingValue[horizontalSpacing]})`;

  const stickyInset = selectable ? selectTrack : undefined;

  const gridTemplateColumns = [
    ...(selectable ? [selectTrack] : []),
    ...table
      .getAllLeafColumns()
      .map((column) => getColumnTrack(column.columnDef.meta)),
  ].join(" ");

  const allSelected = rows.length > 0 && table.getIsAllRowsSelected();

  const togglesOnClick = selectable && selectOnRowClick;

  return (
    <TooltipProvider>
      <div
        data-slot="data-table"
        className={clsx(styles.wrapper, className)}
        {...props}
      >
        <div
          ref={scrollAreaRef}
          className={styles.scrollArea({
            scroll,
            bordered: framed,
            scrollable: ownsScroll && undefined !== maxHeight,
          })}
          style={ownsScroll ? { maxHeight } : undefined}
          aria-busy={loading}
        >
          <div
            role="table"
            ref={tableRef}
            className={styles.table({ contentWidth: stickyColumn })}
            style={
              stickyHeader && undefined !== stickyOffset
                ? { scrollMarginTop: stickyOffset }
                : undefined
            }
          >
            <div
              role="rowgroup"
              ref={headerRef}
              className={styles.headerGroup({ sticky: stickyHeader })}
              style={
                stickyHeader && undefined !== stickyOffset
                  ? { top: stickyOffset }
                  : undefined
              }
            >
              {table.getHeaderGroups().map((headerGroup) => (
                <DataTableRow
                  key={headerGroup.id}
                  header
                  variant={variant}
                  gridTemplateColumns={gridTemplateColumns}
                >
                  {selectable && !enableMultiRowSelection ? (
                    <DataTableSelectCell
                      header
                      size={size}
                      variant={variant}
                      horizontalSpacing={horizontalSpacing}
                      sticky={stickyColumn}
                      opaque={stickyHeader}
                    />
                  ) : selectable ? (
                    <DataTableSelectCell
                      header
                      label={t`Select all rows`}
                      checked={allSelected}
                      indeterminate={
                        !allSelected && table.getIsSomeRowsSelected()
                      }
                      disabled={rows.length === 0}
                      onCheckedChange={(checked) =>
                        table.toggleAllRowsSelected(checked)
                      }
                      size={size}
                      variant={variant}
                      horizontalSpacing={horizontalSpacing}
                      sticky={stickyColumn}
                      opaque={stickyHeader}
                    />
                  ) : null}
                  {headerGroup.headers.map((header, index) => (
                    <DataTableHeaderCell
                      key={header.id}
                      header={header}
                      sticky={stickyColumn && 0 === index}
                      stickyInset={stickyInset}
                      opaque={stickyHeader}
                      sorting={sortingState}
                      columnFilters={filterState}
                      onColumnFiltersChange={
                        onColumnFiltersChange
                          ? (filters) => {
                              revealFirstRow();
                              onColumnFiltersChange(filters);
                            }
                          : undefined
                      }
                      onSortPrefetch={onSortPrefetch}
                      sortable={undefined !== onSortingChange}
                      size={size}
                      variant={variant}
                      columnBorders={columnBorders}
                      horizontalSpacing={horizontalSpacing}
                      verticalSpacing={verticalSpacing}
                    />
                  ))}
                </DataTableRow>
              ))}
            </div>

            <div role="rowgroup">
              {showSkeleton ? (
                <DataTableSkeleton
                  rows={loadingRowCount}
                  rowHeight={loadingRowHeight}
                  columns={leafColumnIds}
                  selectable={selectable}
                  sorting={sortingState}
                  gridTemplateColumns={gridTemplateColumns}
                  size={size}
                  variant={variant}
                  columnBorders={columnBorders}
                  striped={striped}
                  horizontalSpacing={horizontalSpacing}
                  verticalSpacing={verticalSpacing}
                />
              ) : rows.length === 0 ? (
                <div className={styles.emptyRow}>
                  {emptyMessage ?? t`No results.`}
                </div>
              ) : (
                rows.map((row, index) => {
                  const active = index === activeRowIndex;
                  const selected = selectable && row.getIsSelected();

                  const rowElement = (
                    <DataTableRow
                      ref={active ? activeRowRef : undefined}
                      style={
                        activeRowInset
                          ? { scrollMarginTop: activeRowInset }
                          : undefined
                      }
                      gridTemplateColumns={gridTemplateColumns}
                      variant={variant}
                      clickable={Boolean(onRowClick) || togglesOnClick}
                      active={active}
                      selected={selected}
                      aria-selected={selectable ? selected : undefined}
                      detail={Boolean(renderDetail) && row.id === detailRowId}
                      pinned={stickyColumn}
                      striped={striped}
                      onClick={
                        onRowClick || togglesOnClick
                          ? (event) => {
                              tooltipHandle.close();

                              if (togglesOnClick) {
                                row.getToggleSelectedHandler()({
                                  target: { checked: !selected },
                                  shiftKey: event.shiftKey,
                                });
                              }

                              onRowClick?.(row.original, event);
                            }
                          : undefined
                      }
                    >
                      {selectable ? (
                        <DataTableSelectCell
                          label={t`Select row`}
                          checked={selected}
                          disabled={!row.getCanSelect()}
                          onCheckedChange={(checked, shiftKey) =>
                            row.getToggleSelectedHandler()({
                              target: { checked },
                              shiftKey,
                            })
                          }
                          size={size}
                          variant={variant}
                          horizontalSpacing={horizontalSpacing}
                          sticky={stickyColumn}
                        />
                      ) : null}
                      {row.getAllCells().map((cell, cellIndex) => (
                        <DataTableCell
                          key={cell.id}
                          cell={cell}
                          sticky={stickyColumn && 0 === cellIndex}
                          stickyInset={stickyInset}
                          sorting={sortingState}
                          tooltipHandle={tooltipHandle}
                          size={size}
                          columnBorders={columnBorders}
                          horizontalSpacing={horizontalSpacing}
                          verticalSpacing={verticalSpacing}
                        />
                      ))}
                    </DataTableRow>
                  );

                  const menuItems = rowMenu?.(row.original) ?? [];
                  const detail =
                    renderDetail && row.id === detailRowId ? (
                      <div
                        role="row"
                        className={styles.detailRow}
                        data-slot="data-table-detail-row"
                      >
                        <div role="cell">{renderDetail(row.original)}</div>
                      </div>
                    ) : null;

                  return menuItems.length > 0 ? (
                    <Fragment key={row.id}>
                      <DataTableRowMenu
                        items={menuItems}
                        onSelect={(id) => onRowMenuSelect?.(id, row.original)}
                      >
                        {rowElement}
                      </DataTableRowMenu>
                      {detail}
                    </Fragment>
                  ) : (
                    <Fragment key={row.id}>
                      {rowElement}
                      {detail}
                    </Fragment>
                  );
                })
              )}
            </div>
          </div>

          {loading && !showSkeleton && (
            <div className={styles.loadingOverlay}>
              <Loader label={t`Loading`} variant="dots" />
            </div>
          )}
        </div>

        <Tooltip handle={tooltipHandle}>
          {({ payload }) => (
            <TooltipContent className={styles.cellTooltip}>
              <TooltipViewport>{payload}</TooltipViewport>
            </TooltipContent>
          )}
        </Tooltip>
      </div>
    </TooltipProvider>
  );
}
